import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  Add,
  Delete,
  Download,
  LayersFront,
  Link,
  Pencil,
  Replace,
  Suitcase,
} from '@factorialco/f0-react/icons/app'
import { QuotesListTableDocument, type QuotesListTableQuery } from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import { ConvertToProjectModal } from 'modules/finance/components/Quotes/modals/ConvertToProjectModal'
import { CreateQuoteModal } from 'modules/finance/components/Quotes/modals/CreateQuoteModal'
import { quoteStatusAsVariant, quoteTranslateStatus } from 'modules/finance/components/Quotes/utils'
import { useConvertQuoteIntoInvoice } from 'modules/finance/hooks/quotes/useConvertQuoteIntoInvoice'
import { useDeleteQuoteDialog } from 'modules/finance/hooks/quotes/useDeleteQuoteDialog'
import { useDownloadQuotePdf } from 'modules/finance/hooks/quotes/useDownloadQuotePdf'
import { useDuplicateQuote } from 'modules/finance/hooks/quotes/useDuplicateQuote'
import { contactFullName } from 'modules/finance/lib/contactsUtils'

type QuoteNode = QuotesListTableQuery['finance']['quotesConnection']['edges'][number]['node']

export interface QuotesListTableProps {
  companyId: number
  locale: string
  clientId?: number
  dealId?: number
  onClickAdd?: () => void
  showCustomerColumn?: boolean
  canCreateQuote?: boolean
  dealName?: string
}

export const QuotesListTable = ({
  companyId,
  locale,
  clientId,
  dealId,
  onClickAdd,
  showCustomerColumn = false,
  canCreateQuote,
  dealName,
}: QuotesListTableProps) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showConvertToProjectModal, setShowConvertToProjectModal] = useState(false)
  const [selectedQuote, setSelectedQuote] = useState<QuoteNode | null>(null)
  const { showDeleteQuoteDialog } = useDeleteQuoteDialog()

  const convertQuoteIntoInvoice = useConvertQuoteIntoInvoice({
    refetchQueries: [QuotesListTableDocument],
  })

  const duplicateQuote = useDuplicateQuote({ refetchQueries: [QuotesListTableDocument] })

  const downloadQuotePdf = useDownloadQuotePdf()

  const toggleModal = (item: QuoteNode | null) => {
    if (item) {
      setSelectedQuote(item)
    }
    setShowConvertToProjectModal(!showConvertToProjectModal)
  }

  const handleCreateQuote = () => {
    if (canCreateQuote) {
      track(trackingEvents.accounting.quotes.startCreate)
      setShowCreateModal(true)
      return
    }
    if (onClickAdd) {
      onClickAdd()
    }
  }

  const showCreateQuoteAction = canCreateQuote || onClickAdd

  const source = useGraphqlDataCollectionSource({
    document: QuotesListTableDocument,
    mapData: (data) => data.finance.quotesConnection.edges.map((edge) => edge.node),
    search: {
      enabled: true,
    },
    primaryActions: showCreateQuoteAction
      ? () => ({
          label: i18n.t('finance.quotes.actions.create'),
          icon: Add,
          onClick: handleCreateQuote,
        })
      : undefined,
    mapVariables: ({ search }) => ({
      companyId: companyId,
      dealIds: dealId ? [dealId] : undefined,
      clientIds: clientId ? [clientId] : undefined,
      search: search,
    }),
    mapPagination: (data) => ({
      total: data.finance.quotesConnection.totalCount ?? 0,
      hasNextPage: data.finance.quotesConnection.pageInfo?.hasNextPage ?? false,
    }),
    itemUrl: (item) =>
      item._policies?.manage?.can
        ? navigation.finance.sales.quotes.quoteId.fullPath({ quoteId: item.id })
        : navigation.finance.sales.quotes.detail.fullPath({ quoteId: item.id }),
    itemActions: (item) =>
      [
        {
          label: i18n.t('finance.quotes.actions.download_pdf'),
          icon: Download,
          onClick: () => {
            downloadQuotePdf(item.id)
          },
        },
        {
          label: i18n.t('finance.quotes.actions.edit'),
          icon: Pencil,
          onClick: () => {
            navigate(navigation.finance.sales.quotes.quoteId.fullPath({ quoteId: item.id }))
          },
          enabled: item.status === 'draft',
        },
        {
          label: i18n.t('finance.quotes.actions.go_to_invoice'),
          icon: Link,
          onClick: () => {
            navigate(
              navigation.finance.sales.salesInvoices.detail.fullPath({
                invoiceId: Number(item.financialDocumentAssociation?.financialDocument?.id),
              })
            )
          },
          enabled: !!item.financialDocumentAssociation?.financialDocument,
        },
        {
          label: i18n.t('finance.quotes.actions.convert_to_invoice'),
          icon: Replace,
          onClick: () => {
            convertQuoteIntoInvoice(item.id)
          },
          enabled:
            item._policies?.convertToInvoice?.can &&
            item.status !== 'rejected' &&
            !item.financialDocumentAssociation?.financialDocument,
        },
        {
          label: i18n.t('finance.quotes.actions.create_project'),
          icon: Suitcase,
          onClick: () => {
            toggleModal(item)
          },
          enabled: item._policies?.convertToProject?.can,
        },
        {
          label: i18n.t('finance.quotes.actions.duplicate'),
          icon: LayersFront,
          onClick: () => {
            duplicateQuote(item.id)
          },
        },
        {
          label: i18n.t('finance.quotes.actions.delete'),
          icon: Delete,
          critical: true,
          onClick: () => {
            showDeleteQuoteDialog({ quoteId: item.id })
          },
          enabled: item.status === 'draft',
        },
      ].filter((action) => action.enabled !== false),
  })

  const renderQuoteInvoice = (quote: QuoteNode) => {
    const financialDocument = quote.financialDocumentAssociation?.financialDocument
    if (financialDocument) {
      return (
        financialDocument.documentNumber ||
        financialDocument.salesInvoiceSequence?.humanReadablePreview ||
        i18n.t('finance.quotes.columns.invoice_no_number') ||
        '—'
      )
    } else {
      return '—'
    }
  }

  return (
    <>
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('finance.quotes.columns.quote'),
                  render: (item: QuoteNode) => item.quoteNumber || undefined,
                },
                {
                  label: i18n.t('finance.quotes.columns.invoice'),
                  render: (item: QuoteNode) => renderQuoteInvoice(item),
                  info: i18n.t('finance.quotes.columns.invoice_info'),
                },
                {
                  label: i18n.t('finance.quotes.columns.status'),
                  render: (item: QuoteNode) => ({
                    type: 'status' as const,
                    value: {
                      status: quoteStatusAsVariant(item.status),
                      label: quoteTranslateStatus(item.status),
                    },
                  }),
                },
                {
                  label: i18n.t('finance.quotes.columns.amount'),
                  align: 'right' as const,
                  render: (item: QuoteNode) =>
                    item.currency
                      ? formattedAmount({
                          amountCents: Number(item.totalAmountCents),
                          currency: item.currency,
                          locale,
                        })
                      : '-',
                },
                ...(showCustomerColumn
                  ? [
                      {
                        label: i18n.t('finance.quotes.columns.customer'),
                        render: (item: QuoteNode) => contactFullName(item.client),
                      },
                    ]
                  : []),
                {
                  label: i18n.t('finance.quotes.columns.expiration'),
                  render: (item: QuoteNode) =>
                    item.expirationDate
                      ? convertDate(item.expirationDate, 'backend_datetime', 'ui_dmy_long')
                      : '—',
                },
                {
                  label: i18n.t('finance.quotes.columns.created'),
                  render: (item: QuoteNode) =>
                    convertDate(item.createdAt, 'backend_datetime', 'ui_dmy_long'),
                },
              ],
            },
          },
        ]}
      />

      {showCreateModal && (
        <CreateQuoteModal
          clientId={clientId}
          dealId={dealId}
          defaultName={dealName}
          onClose={() => setShowCreateModal(false)}
          isOpen={showCreateModal}
        />
      )}

      {showConvertToProjectModal && selectedQuote && (
        <ConvertToProjectModal
          isOpen={showConvertToProjectModal}
          onClose={() => toggleModal(null)}
          currency={selectedQuote.currency ?? ''}
          quoteId={selectedQuote.id}
          defaultProjectName={selectedQuote.name ?? ''}
          refetchQueries={[QuotesListTableDocument]}
          totalBudget={+selectedQuote.totalAmountCents}
        />
      )}
    </>
  )
}
